import { Box, HStack, IBoxProps, Image, Text, VStack } from 'native-base';
import React, { ReactNode } from 'react';
import { ImageSourcePropType } from 'react-native';
import QRCode from 'react-native-qrcode-svg';

import TicketIcon from 'app/components/icons/Ticket';
import { FONT_SIZE, LOGO_HEIGHT, QR_SIZE } from 'app/components/TicketCarousel/Ticket/constants';
import TicketField from 'app/components/TicketCarousel/TicketField';
import { Locale } from 'app/generated/hygraph';
import { useI18n } from 'app/providers/I18nProvider';
import { TicketCategory, TicketClass } from 'app/services/GuestCenterService.types';
import { cdn, getRatio } from 'app/utils/image';

export type TicketProps = IBoxProps & {
  guestName: string;
  barcodeScan: string;
  barcodeDisplay: string;
  logo: {
    url: string;
    width: number;
    height: number;
  };
  barcodeLogo?: string;
  ticketName: string;
  ageRange: Maybe<string>;
  ticketClass: TicketClass | undefined;
  hideAge?: boolean;
  children?: ReactNode;
  orderNumber?: string;
  ticketCategory?: TicketCategory;
};

export default function Ticket({
  logo,
  ticketName,
  barcodeScan,
  barcodeDisplay,
  barcodeLogo,
  guestName,
  ageRange,
  ticketClass,
  hideAge,
  children,
  orderNumber,
  ...props
}: TicketProps) {
  const { t } = useI18n();

  const defaultLocale = Locale.En;
  const ticketColor = ticketClass === TicketClass.Adult ? 'black' : 'red';
  const logoRatio = getRatio(logo, 2);
  const logoUrl = logo?.url
    ? cdn(logo.url, [
        ['output', { format: 'png' }],
        ['resize', { height: LOGO_HEIGHT * 2 }],
      ])
    : '';
  const barcodeLogoImage = barcodeLogo ? (barcodeLogo as ImageSourcePropType) : undefined;

  return (
    <Box testID="TicketCard" {...props}>
      <Box bg="white" p="6" pb="4" rounded="lg" shadow="5">
        <VStack space="4" w="full">
          <HStack alignItems="center" justifyContent="space-between">
            {!!logoUrl && (
              <Box h={`${LOGO_HEIGHT}px`} style={{ aspectRatio: logoRatio }}>
                <Image
                  alt="Logo"
                  h="full"
                  resizeMode="contain"
                  source={{ uri: logoUrl }}
                  w="full"
                />
              </Box>
            )}

            {!hideAge && !!ticketClass && (
              <VStack alignItems="center" flexShrink="1" justifyContent="center" w={`${QR_SIZE}px`}>
                <TicketIcon color={ticketColor} size={LOGO_HEIGHT} />
                <Box position="absolute">
                  <Text
                    bold
                    color="white"
                    fontSize={FONT_SIZE}
                    lineHeight={FONT_SIZE}
                    textAlign="center"
                  >
                    {ticketClass}
                  </Text>
                  {!!ageRange && (
                    <Text
                      bold
                      color="white"
                      fontSize={FONT_SIZE}
                      lineHeight={FONT_SIZE}
                      textAlign="center"
                    >
                      ({ageRange})
                    </Text>
                  )}
                </Box>
              </VStack>
            )}
          </HStack>

          <HStack alignItems="flex-start" justifyContent="space-between" space="2">
            <VStack flexShrink="1" space="4">
              <TicketField
                label={t('app_guest_title', { locale: defaultLocale })}
                value={guestName}
              />
              <TicketField
                label={t('app_ticket_title', { locale: defaultLocale })}
                value={ticketName}
              />
              {orderNumber && (
                <TicketField
                  label={t('app_order_number_title', { locale: defaultLocale })}
                  selectable
                  value={orderNumber}
                />
              )}
            </VStack>

            <VStack alignItems="center" maxWidth={QR_SIZE + 20} space="1">
              <QRCode
                logo={barcodeLogoImage}
                logoMargin={30}
                logoSize={35}
                size={QR_SIZE}
                value={barcodeScan}
              />
              <Text fontSize={FONT_SIZE} selectable textAlign="center">
                {barcodeDisplay ?? barcodeScan}
              </Text>
            </VStack>
          </HStack>

          {children}
          {/* <Dump data={{ pass }} /> */}
        </VStack>
      </Box>
    </Box>
  );
}
